import classNames from 'classnames';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';

import {
  activateFeatureTour,
  completeCurrentTour,
  hideFeatureTour,
  setRemainingTours,
  showLowFrictionTourOnLoad,
  updateSharePageVisitCount,
} from 'actions/feature-tour';
import { LOOM_PRO } from 'constants/capabilities';
import {
  VIDEO_OWNER_ONBOARDING_COMPLETE,
  VIDEO_OWNER_ONBOARDING_REENGAGE,
  VIDEO_OWNER_ONBOARDING_SKIP,
} from 'constants/events';
import {
  COMPLETED_TOUR_DATA,
  ORDERED_TOURS,
  ORDERED_TOURS_PRO,
  TOUR_DATA,
} from 'constants/feature-tour';
import * as analytics from 'utilities/analytics';

import closeButtonSrc from 'img/icons/new/close-dg.svg';
import helpBubbleSrc from 'img/onboarding/elevio-icon.png';

import Carousel from 'components/carousel';
import Button from 'components/ui-components/button';

import styles from './styles.module.less';

const HighFrictionTip = ({ icon, title, description, helpIcon }) => (
  <div className={styles.contentContainer}>
    {helpIcon ? (
      <div className={styles.helpIcon}>
        <img src={helpBubbleSrc} />
      </div>
    ) : (
      <img className={styles.icon} src={icon} />
    )}
    <h3 className={styles.heading}>{title}</h3>
    <p className={styles.description}>{description}</p>
  </div>
);

class FeatureTour extends Component {
  componentDidMount() {
    const { tourSettings } = this.props;
    const { sharePageVisitCount = 0 } = tourSettings;
    const currentVisitCount = sharePageVisitCount + 1;

    if (currentVisitCount > 10) {
      return;
    }

    setTimeout(() => {
      if ([2, 5, 10].includes(currentVisitCount)) {
        this.props.showLowFrictionTourOnLoad();
      }
    }, 3000);

    this.props.updatePageVisitCount(currentVisitCount);
  }

  completeTour = () => {
    this.props.completeActiveTour();
    this.props.hideFeatureTour();

    analytics.track(VIDEO_OWNER_ONBOARDING_COMPLETE, {
      revisiting: this.props.revisitingTour,
    });
  };

  onUserClose = () => {
    this.props.hideFeatureTour();

    analytics.track(VIDEO_OWNER_ONBOARDING_SKIP, {
      exit_step: this.props.currentTour,
      revisiting: this.props.revisitingTour,
    });
  };

  engageTour = () => {
    this.props.activateTour();

    analytics.track(VIDEO_OWNER_ONBOARDING_REENGAGE, {
      source: this.props.currentTour,
    });
  };

  render() {
    const {
      currentTour,
      tourOpen,
      tourActive,
      hasMoreTours,
      hasLoomPro,
    } = this.props;

    const containerClass = classNames(
      styles.container,
      !tourActive && styles.minimized,
      tourOpen && styles.tourOpen
    );

    const closeButtonClass = classNames(
      styles.closeButton,
      tourOpen && !tourActive && styles.show
    );

    const currentTourData = hasMoreTours
      ? TOUR_DATA[currentTour]
      : COMPLETED_TOUR_DATA;

    const { longTitle, iconSrc } = currentTourData;

    const toursArr = hasLoomPro ? ORDERED_TOURS_PRO : ORDERED_TOURS;

    return (
      <div className={containerClass}>
        {tourActive && (
          <Carousel selectedIndex={toursArr.indexOf(currentTour)}>
            {toursArr.map(tour => {
              const tourTip = TOUR_DATA[tour];

              return (
                <HighFrictionTip
                  key={tour}
                  icon={tourTip.iconSrc}
                  title={tourTip.title}
                  description={tourTip.description}
                />
              );
            })}
          </Carousel>
        )}

        {tourActive && (
          <div
            className={classNames(
              styles.completeTip,
              !currentTour && styles.show
            )}
          >
            <HighFrictionTip
              helpIcon={false}
              icon={COMPLETED_TOUR_DATA.iconSrc}
              title={COMPLETED_TOUR_DATA.title}
              description={COMPLETED_TOUR_DATA.description}
            />
            <div className={styles.buttonContainer}>
              <Button
                buttonType={classNames('rp-primary-4', styles.actionButton)}
                value="Done"
                onClicked={this.completeTour}
              />
            </div>
          </div>
        )}

        {!tourActive && (
          <Fragment>
            <img className={styles.icon} src={iconSrc} />
            <h3 className={styles.longHeading}>{longTitle}</h3>
            <div className={styles.tryButton} onClick={this.engageTour}>
              Try it
            </div>
            <img
              onClick={this.onUserClose}
              className={closeButtonClass}
              src={closeButtonSrc}
            />
          </Fragment>
        )}

        <div className={styles.buttonContainer}>
          {hasMoreTours && (
            <Fragment>
              <Button
                buttonType={classNames(
                  'rp-secondary-4',
                  styles.actionButton,
                  styles.laterButton
                )}
                value="I’ll do it later"
                capitalize={false}
                onClicked={this.onUserClose}
              />
              <Button
                buttonType={classNames('rp-secondary-4', styles.actionButton)}
                value="Next tip"
                onClicked={this.props.completeActiveTour}
              />
            </Fragment>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const { featureTour } = state;
  const { capabilities } = state.user.current;

  const {
    currentTour,
    tourOpen,
    tourActive,
    remainingTours,
    revisitingTour,
    settings: tourSettings,
  } = featureTour;

  const hasMoreTours = Boolean(remainingTours.length);
  const hasLoomPro = capabilities[LOOM_PRO];

  return {
    currentTour,
    tourOpen,
    tourActive,
    hasMoreTours,
    tourSettings,
    hasLoomPro,
    revisitingTour,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    activateTour: () => dispatch(activateFeatureTour()),
    showLowFrictionTourOnLoad: () => dispatch(showLowFrictionTourOnLoad()),
    hideFeatureTour: () => dispatch(hideFeatureTour()),
    completeActiveTour: () => dispatch(completeCurrentTour()),
    setRemainingTours: () => dispatch(setRemainingTours()),
    updatePageVisitCount: newCount =>
      dispatch(updateSharePageVisitCount(newCount)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FeatureTour);
